import { useEffect, useState } from "react";

import { Button, InfoBadge, InfoPage, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useBlocker, useNavigate, useSearchParams } from "react-router-dom";

import { sessionStorageKeys } from "../config";
import useNavigation from "../hooks/navigation";
import useAccount from "../hooks/useAccount";
import { activeSubscriptionQueryKey } from "../hooks/useTeamActiveSubscription";
import { useTracker } from "../hooks/useTracker";
import useTranslation from "../hooks/useTranslation";
import { ElementClickAction } from "../lib/TrackingAdapter";
import { SearchParam } from "../types";
import { sanitizeRedirectUrl } from "../urlUtils";

const NAVIGATION_DELAY = 2000;

export const TeamPaymentSuccessPage: React.FC = () => {
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const [searchParams] = useSearchParams();
  const routerNavigate = useNavigate();
  const tracker = useTracker();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();

  const isWithInvite = searchParams.get(SearchParam.WithInvite) === "true";
  const accountTeamID = accountData?.accountTeamID ?? accountData?.teams[0].id ?? "";
  const [shouldBlock, setShouldBlock] = useState(true);
  const queryClient = useQueryClient();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname !== nextLocation.pathname) {
      sessionStorage.removeItem(sessionStorageKeys.checkoutReturnUrl);
    }
    return shouldBlock;
  });

  useEffect(() => {
    const storedReturnPath = sessionStorage.getItem(sessionStorageKeys.checkoutReturnUrl);
    if (!storedReturnPath) {
      setShouldBlock(false);
      return;
    }
    const sanitized = sanitizeRedirectUrl(storedReturnPath);
    const targetPath = `${sanitized.pathname}${sanitized.search}${sanitized.hash}`;

    const timeoutId = window.setTimeout(() => {
      setShouldBlock(false);
      routerNavigate(targetPath, { replace: true });
    }, NAVIGATION_DELAY);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [blocker, routerNavigate]);

  useEffect(() => {
    if (!shouldBlock && blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker, shouldBlock]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: activeSubscriptionQueryKey });
  }, [queryClient]);

  if (isAccountLoading) {
    return <SpinnerCenter />;
  }

  if (!accountData) {
    return <Navigate to="/" />;
  }

  return (
    <InfoPage
      variant="success"
      title={t(m.MembersPage.TeamAllSet)}
      description={t(m.MembersPage.TeamAllSetDescription)}
      primaryCTA={
        <Button
          onClick={() => {
            tracker.trackButtonClick({
              action: ElementClickAction.GO_TO_DOWNLOAD,
            });
            navigation.goToDownloadPage();
          }}
        >
          {t(m.MembersPage.DownloadShapr3D)}
        </Button>
      }
      footer={
        <InfoBadge
          standalone={true}
          description={t(m.MembersPage.InviteAndManage)}
          cta={
            <Button
              variant="grayscale"
              onClick={() => {
                setShouldBlock(false);
                navigation.goToTeamMembers({
                  teamID: accountTeamID,
                  omitQueryParams: [SearchParam.WithInvite, SearchParam.CheckoutShowPeriodPicker],
                  additionalQueryParams: !isWithInvite
                    ? {
                        [SearchParam.Intent]: "invite",
                      }
                    : undefined,
                });
              }}
            >
              {isWithInvite ? t(m.MembersPage.GoToMembers) : t(m.MembersPage.InviteMembers)}
            </Button>
          }
        />
      }
    />
  );
};
